/**
 * 1.7.8 (D-1.7.8-1) — the When control: Now (one tap) · a 30-minute chip row across the day · Exact time.
 *
 * One radio group: Now, the suggested time (a free gap, the booked time) and every half hour of the day. The chips
 * are buttons with role="radio" — the arrow keys move and choose, Tab enters and leaves the group once, the chosen
 * one carries aria-checked and a check mark (never colour alone). The chip row scrolls sideways inside its own box.
 * The exact field takes any minute. With `allowFuture={false}` (Start / Arrived) a time still to come is not
 * offered: its chips are disabled and the exact field refuses it in words, and nothing is passed up.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { whenChips } from '@shared/day-bend';
import { clockWithPeriod, formatClock } from '@shared/dayplan';
import { serviceMinutesNow } from '@shared/dates';
import { Icon } from '@/components/Icons';
import { useDayPlan } from '@/lib/queries';
import { useNow } from '@/features/press/Press';

export type WhenValue = { mode: 'now' } | { mode: 'time'; minutes: number };

export interface WhenControlProps {
  /** yyyyMMdd — the chips come from that day's plan (shared/day-bend.ts whenChips). */
  day: string;
  value: WhenValue;
  onChange: (v: WhenValue) => void;
  /** Unique on the page: ids are `<id>-now`, `<id>-chips`, `<id>-exact`. */
  id: string;
  /** The words of the first choice: "Now" (default). */
  nowLabel?: string;
  /** False on Start / Arrived: a time still to come is not offered (its chips are disabled, the exact field refuses it). */
  allowFuture?: boolean;
  /** A time to offer first, with its own words (a free gap: "Fits the 2:30 gap"; the booked time on Start). */
  suggest?: { minutes: number; label: string } | null;
}

const hhmm = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
/** "14:47" → 887; null for a field that is empty or half typed. */
function parseTime(v: string): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(v);
  if (!m) return null;
  const n = Number(m[1]) * 60 + Number(m[2]);
  return n >= 0 && n < 24 * 60 ? n : null;
}

export function WhenControl({ day, value, onChange, id, nowLabel, allowFuture = true, suggest }: WhenControlProps) {
  const { t, i18n } = useTranslation();
  const lang: 'en' | 'es' = i18n.language.startsWith('es') ? 'es' : 'en';
  const settings = useDayPlan().data?.settings;
  const chips = whenChips(day, settings ?? {});
  const now = serviceMinutesNow(useNow(30_000));
  // nowLabel="" — the caller wants no Now (the Move sheet): the choice is left out
  const showNow = nowLabel !== '';
  const chosen = value.mode === 'time' ? value.minutes : null;
  const onChip = chosen !== null && (chips.includes(chosen) || suggest?.minutes === chosen);
  /** What stands in the exact field: the person's own typing (a refused time stays, with its words). */
  const [draft, setDraft] = useState(() => (chosen !== null && !onChip ? hhmm(chosen) : ''));
  const typed = parseTime(draft);
  const refused = !allowFuture && typed !== null && typed > now ? typed : null;
  const group = useRef<HTMLDivElement | null>(null);
  const row = useRef<HTMLDivElement | null>(null);

  const tooLate = (m: number) => !allowFuture && m > now;
  const pick = (v: WhenValue) => {
    setDraft('');
    onChange(v);
  };

  // the row opens where the day is: the chosen chip, else the half hour now is in (its own box only — never the page)
  useEffect(() => {
    const box = row.current;
    if (!box) return;
    const at = chosen !== null && chips.includes(chosen) ? chosen : (chips.filter((m) => m <= now).pop() ?? null);
    const el = at === null ? null : box.querySelector<HTMLElement>(`[data-minutes="${at}"]`);
    if (el) box.scrollLeft = Math.max(0, el.offsetLeft - box.offsetLeft - 8);
    // once, when the control appears
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const radios = () => [...(group.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(:disabled)') ?? [])];
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step && e.key !== 'Home' && e.key !== 'End') return;
    const all = radios();
    if (!all.length) return;
    const i = all.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === 'Home' ? all[0]! : e.key === 'End' ? all[all.length - 1]! : all[(Math.max(i, 0) + step + all.length) % all.length]!;
    e.preventDefault();
    next.focus();
    next.click(); // a radio group: moving chooses
  };

  // one stop for Tab: the chosen radio, else the first one that can be chosen
  const nowOn = showNow && value.mode === 'now';
  const suggestOn = !!suggest && chosen === suggest.minutes;
  const firstFree = showNow ? 'now' : suggest && !tooLate(suggest.minutes) ? 'suggest' : (chips.find((m) => !tooLate(m)) ?? null);
  const stop: 'now' | 'suggest' | number | null = nowOn ? 'now' : suggestOn ? 'suggest' : chosen !== null && chips.includes(chosen) ? chosen : firstFree;

  const says =
    chosen === null
      ? showNow
        ? t('when178.saysNow', { time: clockWithPeriod(now, lang) })
        : t('when178.saysNone')
      : t(chosen < now ? 'when178.saysEarlier' : chosen > now ? 'when178.saysLater' : 'when178.saysSame', { time: clockWithPeriod(chosen, lang) });

  const radio = (key: 'now' | 'suggest' | number, on: boolean, words: string, o: { testid: string; disabled?: boolean; label?: string; onPick: () => void; elId?: string }) => (
    <button
      key={key}
      type="button"
      role="radio"
      id={o.elId}
      className="chip when-chip"
      aria-checked={on}
      aria-label={o.label}
      disabled={o.disabled}
      tabIndex={stop === key ? 0 : -1}
      data-minutes={typeof key === 'number' ? key : undefined}
      data-testid={o.testid}
      onClick={o.onPick}
    >
      {on ? <Icon name="check" aria-hidden="true" /> : null}
      <span>{words}</span>
    </button>
  );

  return (
    <div className="field when178" data-testid="when-control">
      <span className="when-label" id={`${id}-label`}>
        {t('when178.label')}
      </span>
      <div className="when-group" role="radiogroup" aria-labelledby={`${id}-label`} aria-describedby={`${id}-says`} ref={group} onKeyDown={onKeyDown}>
        {showNow || suggest ? (
          <div className="when-first">
            {showNow ? radio('now', nowOn, nowLabel ?? t('when178.now'), { testid: 'when-now', elId: `${id}-now`, onPick: () => pick({ mode: 'now' }) }) : null}
            {suggest ? radio('suggest', suggestOn, suggest.label, { testid: 'when-suggest', disabled: tooLate(suggest.minutes), onPick: () => pick({ mode: 'time', minutes: suggest.minutes }) }) : null}
          </div>
        ) : null}
        {/* a row that scrolls must be reachable by keyboard: when the group's one tab stop is not a chip, the row itself is */}
        <div className="when-chips" id={`${id}-chips`} ref={row} data-testid="when-chips" role="group" aria-label={t('when178.chipsLabel')} tabIndex={typeof stop === 'number' ? undefined : 0}>
          {chips.map((m) =>
            radio(m, chosen === m, formatClock(m), {
              testid: `when-chip-${m}`,
              disabled: tooLate(m),
              label: tooLate(m) ? t('when178.notYet', { time: clockWithPeriod(m, lang) }) : clockWithPeriod(m, lang),
              onPick: () => pick({ mode: 'time', minutes: m }),
            }),
          )}
        </div>
      </div>
      <div className="when-exact">
        <label htmlFor={`${id}-exact`}>{t('when178.exact')}</label>
        <input
          id={`${id}-exact`}
          type="time"
          className="input"
          value={draft}
          max={allowFuture ? undefined : hhmm(now)}
          aria-invalid={refused !== null ? true : undefined}
          aria-describedby={`${id}-exact-help${refused !== null ? ` ${id}-future` : ''}`}
          data-testid="when-exact"
          onChange={(e) => {
            const v = e.target.value;
            setDraft(v);
            const m = parseTime(v);
            if (m === null) {
              // the field was emptied: back to Now where there is one (an exact time that is no longer there is not kept)
              if (showNow && chosen !== null && !onChip) onChange({ mode: 'now' });
              return;
            }
            if (!allowFuture && m > now) return; // refused in words below; nothing is passed up
            onChange({ mode: 'time', minutes: m });
          }}
        />
        <span className="help" id={`${id}-exact-help`}>
          {t('when178.exactHelp')}
        </span>
      </div>
      {refused !== null ? (
        <p className="field-err" role="alert" id={`${id}-future`} data-testid="when-future">
          <Icon name="alert" aria-hidden="true" />
          <span>{t('when178.future', { time: clockWithPeriod(refused, lang) })}</span>
        </p>
      ) : null}
      <p className="when-says" id={`${id}-says`} data-testid="when-says">
        {says}
      </p>
    </div>
  );
}
